"use client";

import { useState, useRef } from "react";
import {
  Upload, FileText, AlertTriangle, CheckCircle, Info, ArrowRight,
  Receipt, HeartPulse, Shield, TrendingUp, Eye, Sparkles, Printer,
} from "lucide-react";
import { T, useTextLocale, tl } from "@/components/t";
import {
  FinancePage, SectionHeader, Tint, Tile, StatGrid, RecoLink, SplitBar, Legend,
} from "@/components/finance/Finance";
import {
  Button, Card, Badge, Icon, EmptyState, Stack,
} from "@/design";
import { logger } from "@/lib/logger";
import { track } from "@/lib/analytics";
import { storage } from "@/lib/storage";
import { useFlag } from "@/lib/useFlag";
import SollIstCheck from "./SollIstCheck";
import styles from "./FinanzenPayslipV2.module.css";
import PayslipVerlauf from "./PayslipVerlauf";
import BenefitRadar from "./BenefitRadar";

const API = process.env.NEXT_PUBLIC_API_URL || "";

interface Deduction {
  label: string; amount: number; category: string;
  explanation: string; is_optimizable: boolean;
  optimization_tip?: string;
}
interface Optimization {
  title: string; potential_savings_yearly: number;
  effort: string; description: string; next_step: string;
}
interface PayslipResult {
  summary: {
    brutto: number; netto: number; deduction_pct: number; month?: string;
    employer?: string; tax_class?: string; kv_zusatz?: number; bundesland?: string;
    headline: string;
  };
  deductions: Deduction[];
  totals: { total_taxes: number; total_social: number; total_deductions: number; tax_pct: number; social_pct: number };
  what_you_get: { label: string; value: string }[];
  optimization_opportunities: Optimization[];
  red_flags?: { title: string; description: string }[];
  vergleich_zu_gleichaltrigen?: { income_percentile: number; deduction_percentile: number; context: string };
  is_example?: boolean;
}

export default function FinanzenPayslipV2() {
  const loc = useTextLocale();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PayslipResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Payslip → Finanz-DNA autofill (finance P1): carry the recognized brutto/netto
  // into the Finanz-Score instead of asking the user to retype them in /finanzen.
  const financeMoments = useFlag("finance.moments", false);
  const [dnaApplied, setDnaApplied] = useState(false);
  const applyToDna = () => {
    if (!result) return;
    const existing = storage.getJSON<Record<string, unknown> | null>("careerkit_finanz_dna", null);
    storage.setJSON("careerkit_finanz_dna", {
      ...(existing || {}),
      monthly_brutto: Math.round(result.summary.brutto),
      monthly_net: Math.round(result.summary.netto),
    });
    setDnaApplied(true);
    track("finanzen_payslip_dna_apply", { had_dna: !!existing });
  };

  const onPick = (f: File) => {
    setFile(f);
    setError(null);
    setResult(null);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const upload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setDnaApplied(false);
    track("finanzen_payslip_explain", { type: file.type });
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/api/finance/payslip/explain`, { method: "POST", body: fd });
      if (!r.ok) {
        let detail = "";
        try {
          const err = await r.json();
          detail = err.detail || "";
        } catch (e) {
          logger.warn("finanzen.payslip.parse_error", { error: String(e) });
        }
        throw new Error(detail || tl(loc, "Analyse fehlgeschlagen", "Analysis failed"));
      }
      const data = (await r.json()) as PayslipResult;
      // p4-payslip-extras: lokaler Gehalts-Verlauf (nur Monat/Brutto/Netto, max 24, dedupe je Monat)
      try {
        if (!data.is_example && data.summary && data.summary.brutto > 0) {
          const month = (data.summary.month || "").trim();
          const prev = storage.getJSON<{ month: string; brutto: number; netto: number; ts: number }[]>("ck_payslip_history", []);
          const clean = (Array.isArray(prev) ? prev : []).filter((h) => h && typeof h.brutto === "number");
          const deduped = month ? clean.filter((h) => (h.month || "").trim().toLowerCase() !== month.toLowerCase()) : clean;
          storage.setJSON("ck_payslip_history", [...deduped, { month, brutto: data.summary.brutto, netto: data.summary.netto, ts: Date.now() }].slice(-24));
        }
      } catch (e) {
        logger.warn("finanzen.payslip.history_failed", { error: String(e) });
      }
      setResult(data);
    } catch (e) {
      logger.error("finanzen.payslip.explain_failed", { error: String(e) });
      setError((e as Error).message);
    }
    setLoading(false);
  };

  const showExampleData = async () => {
    setLoading(true);
    track("finanzen_payslip_example");
    try {
      const r = await fetch(`${API}/api/finance/payslip/example`);
      if (r.ok) setResult(await r.json());
    } catch (e) {
      logger.error("finanzen.payslip.example_failed", { error: String(e) });
    }
    setLoading(false);
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setOpenIdx(null);
    setDnaApplied(false);
  };

  /* ── Result view ── */
  if (result) {
    const abzug = result.summary.brutto - result.summary.netto;
    return (
      <FinancePage
        backHref="/finanzen"
        backLabel={<T de="Finanzwissen" en="Finance" />}
        eyebrow={<T de="Lohnabrechnung" en="Payslip" />}
        title={result.summary.headline}
        lead={result.summary.month
          ? `${result.summary.month}${result.summary.employer ? ` · ${result.summary.employer}` : ""}`
          : <T de="Jede Position deiner Gehaltsabrechnung erklärt." en="Every line of your payslip explained." />}
        wide
      >
        <div id="ck-payslip-print">
        {/* p4-payslip-extras: Druck-Styles — im Druck nur das Ergebnis, App-Chrome + Buttons raus */}
        <style href="ck-finanzenpayslipv2-1" precedence="ck-inline">{`@media print{body *{visibility:hidden}#ck-payslip-print,#ck-payslip-print *{visibility:visible}#ck-payslip-print{position:absolute;left:0;top:0;width:100%}#ck-payslip-print [data-noprint]{display:none !important}}`}</style>
        <Stack gap="6">
          <div className={styles.topActions} data-noprint="">
            <Button variant="secondary" size="sm" onClick={reset} leftIcon={<Icon icon={Upload} decorative />}>
              <T de="Andere Lohnabrechnung" en="Different payslip" />
            </Button>
          </div>

          {result.is_example && (
            <Tint tone="warn">
              <div className={styles.inlineNote}>
                <Icon icon={Info} decorative />
                <span><T de="Das ist ein Beispiel. Lade deine eigene Lohnabrechnung hoch, um deine Zahlen zu sehen." en="This is an example. Upload your own payslip to see your numbers." /></span>
              </div>
            </Tint>
          )}

          {/* Hero numbers */}
          <StatGrid cols={3}>
            <Tile
              label={<T de="Brutto" en="Gross" />}
              value={`${result.summary.brutto.toLocaleString("de-DE")}€`}
            />
            <Tile
              tone="neg"
              label={<T de="Abzüge" en="Deductions" />}
              value={`−${abzug.toLocaleString("de-DE")}€`}
              sub={`(${result.summary.deduction_pct}%)`}
            />
            <Tile
              tone="pos"
              label={<T de="Netto" en="Net" />}
              value={`${result.summary.netto.toLocaleString("de-DE")}€`}
            />
          </StatGrid>

          {/* Payslip → Finanz-DNA handoff (finance P1) */}
          {financeMoments && !result.is_example && (
            <Tint tone="green">
              {dnaApplied ? (
                <Stack gap="3">
                  <div className={styles.inlineNote}>
                    <Icon icon={CheckCircle} decorative />
                    <span><T de="Übernommen — dein Finanz-Score rechnet jetzt mit deinen echten Zahlen." en="Saved — your finance score now uses your real numbers." /></span>
                  </div>
                  <RecoLink
                    href="/finanzen"
                    title={<T de="Finanz-Score aktualisieren" en="Update your finance score" />}
                    desc={<T de="Rente, Sparen, Versicherungen — gerechnet mit deinem echten Netto." en="Pension, savings, insurance — based on your real net pay." />}
                  />
                  <RecoLink
                    href="/schutz-zwilling"
                    title={<T de="Schutz-Check öffnen" en="Open the coverage check" />}
                    desc={<T de="Bist du für dein Einkommen richtig abgesichert?" en="Are you properly covered for this income?" />}
                  />
                </Stack>
              ) : (
                <div className={styles.inlineNote} style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                  <span style={{ flex: "1 1 240px" }}>
                    <T de="Diese Zahlen können deinen Finanz-Score füttern — Brutto und Netto werden direkt übernommen." en="These numbers can feed your finance score — gross and net are carried over directly." />
                  </span>
                  <Button size="sm" onClick={applyToDna}>
                    <T de="In Finanz-Score übernehmen" en="Use in my finance score" />
                  </Button>
                </div>
              )}
            </Tint>
          )}

          {/* Soll-Ist-Abgleich (finance W2 p1): payslip reality vs. engine expectation */}
          <SollIstCheck result={result} />

          {/* Red flags first */}
          {result.red_flags && result.red_flags.length > 0 && (
            <div>
              <SectionHeader title={<T de="Auffälligkeiten" en="Irregularities" />} />
              <Tint tone="red">
                <Stack gap="3">
                  {result.red_flags.map((f, i) => (
                    <div key={i}>
                      <p className={styles.flagTitle}>{f.title}</p>
                      <p className={styles.flagDesc}>{f.description}</p>
                    </div>
                  ))}
                </Stack>
              </Tint>
            </div>
          )}

          {/* Each deduction explained */}
          <div>
            <SectionHeader
              title={<T de="Jede Abzug-Position erklärt" en="Every deduction explained" />}
              hint={<T de="Tippe für Details" en="Tap for details" />}
            />
            <Card variant="muted">
              <div className={styles.dedList}>
                {result.deductions.map((d, i) => {
                  const open = openIdx === i;
                  const tone = d.category === "steuer" ? "red" : d.category === "sozialversicherung" ? "brand" : "default";
                  return (
                    <div key={i} className={styles.dedItem}>
                      <button
                        type="button"
                        className={styles.dedRow}
                        aria-expanded={open}
                        onClick={() => setOpenIdx(open ? null : i)}
                      >
                        <span className={`${styles.dedIcon} ${styles[`tone_${tone}`]}`}>
                          <Icon icon={d.category === "steuer" ? Receipt : HeartPulse} decorative />
                        </span>
                        <span className={styles.dedLabel}>{d.label}</span>
                        <span className={styles.dedAmount}>
                          −{d.amount.toLocaleString("de-DE", { minimumFractionDigits: 2 })}€
                        </span>
                        {d.is_optimizable && (
                          <Badge tone="success"><T de="Sparbar" en="Saveable" /></Badge>
                        )}
                      </button>
                      {open && (
                        <div className={styles.dedBody}>
                          <p className={styles.dedExpl}>{d.explanation}</p>
                          {d.optimization_tip && (
                            <Tint tone="green">
                              <p className={styles.tipLabel}><T de="Spar-Tipp" en="Saving tip" /></p>
                              <p className={styles.tipText}>{d.optimization_tip}</p>
                            </Tint>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* p4-payslip-extras: Drucken + Verlauf + Benefit-Radar — nach der Abzugsliste */}
          <div className={styles.topActions} style={{ justifyContent: "flex-end" }} data-noprint="">
            <Button variant="secondary" size="sm" onClick={() => { track("payslip_print"); window.print(); }} leftIcon={<Icon icon={Printer} decorative />}>
              <T de="Drucken / PDF" en="Print / PDF" />
            </Button>
          </div>
          <PayslipVerlauf deductions={result.deductions} example={!!result.is_example} />
          <BenefitRadar deductions={result.deductions} whatYouGet={result.what_you_get} example={!!result.is_example} />

          {/* What you get */}
          {result.what_you_get?.length > 0 && (
            <div>
              <SectionHeader title={<T de="Was du für deine Sozialabgaben bekommst" en="What you get for your social contributions" />} />
              <div className={styles.getGrid}>
                {result.what_you_get.map((w, i) => (
                  <Card key={i} variant="muted">
                    <div className={styles.getItem}>
                      <span className={styles.getIcon}><Icon icon={CheckCircle} decorative /></span>
                      <div>
                        <p className={styles.getLabel}>{w.label}</p>
                        <p className={styles.getValue}>{w.value}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Optimization opportunities */}
          {result.optimization_opportunities?.length > 0 && (
            <div>
              <SectionHeader
                title={<T de="Hier kannst du sparen" en="Here you can save" />}
                hint={<T de="Konkrete Hebel" en="Concrete levers" />}
              />
              <Stack gap="4">
                {result.optimization_opportunities.map((o, i) => (
                  <Tint key={i} tone="green">
                    <div className={styles.optHead}>
                      <div>
                        <p className={styles.optTitle}>{o.title}</p>
                        <p className={styles.optEffort}>
                          <T de="Aufwand:" en="Effort:" /> {o.effort}
                        </p>
                      </div>
                      <p className={styles.optSaving}>
                        +{o.potential_savings_yearly.toLocaleString("de-DE")}€
                        <span className={styles.optPer}>/<T de="Jahr" en="year" /></span>
                      </p>
                    </div>
                    <p className={styles.optDesc}>{o.description}</p>
                    <p className={styles.optNext}>→ {o.next_step}</p>
                  </Tint>
                ))}
              </Stack>
            </div>
          )}

          {/* Peer comparison */}
          {result.vergleich_zu_gleichaltrigen && (
            <div>
              <SectionHeader
                title={<T de="Du vs. andere unter 30" en="You vs. others under 30" />}
                hint={result.vergleich_zu_gleichaltrigen.context}
              />
              <Card>
                <div className={styles.peerWrap}>
                  <SplitBar
                    segments={[
                      { pct: 100 - result.vergleich_zu_gleichaltrigen.income_percentile, color: "var(--ck-primary)" },
                      { pct: result.vergleich_zu_gleichaltrigen.income_percentile, color: "var(--ck-secondary)" },
                    ]}
                  />
                  <Legend
                    items={[
                      { color: "var(--ck-primary)", label: tl(loc, `Einkommen: Top ${100 - result.vergleich_zu_gleichaltrigen.income_percentile}%`, `Income: Top ${100 - result.vergleich_zu_gleichaltrigen.income_percentile}%`) },
                      { color: "var(--ck-secondary)", label: tl(loc, `Abzugsquote: ${result.vergleich_zu_gleichaltrigen.deduction_percentile}. Perzentil`, `Deduction rate: ${result.vergleich_zu_gleichaltrigen.deduction_percentile}th percentile`) },
                    ]}
                  />
                </div>
              </Card>
            </div>
          )}

          {/* CTA back to dashboard */}
          <RecoLink
            href="/finanzen"
            icon={<Icon icon={ArrowRight} decorative />}
            iconTone="default"
            title={<T de="Sieh deinen Finanz-Score und personalisierte Tipps" en="See your financial score and personalised tips" />}
            desc={<T de="Zurück zum Finanzwissen-Dashboard" en="Back to the finance dashboard" />}
          />
        </Stack>
        </div>
      </FinancePage>
    );
  }

  /* ── Upload view ── */
  return (
    <FinancePage
      backHref="/finanzen"
      backLabel={<T de="Finanzwissen" en="Finance" />}
      eyebrow={<T de="Lohnabrechnung verstehen" en="Understand your payslip" />}
      title={<T de="Was bedeutet jede Zeile auf deiner Gehaltsabrechnung?" en="What does every line on your payslip mean?" />}
      lead={<T de="Lade deine Lohnabrechnung hoch, wir erklären dir jede Position und zeigen, wo du sparen kannst." en="Upload your payslip, we explain every item and show where you can save." />}
    >
      <Stack gap="6">
        {/* Upload zone */}
        {file ? (
          <Card>
            <Stack gap="4">
              {preview ? (
                <img src={preview} alt={tl(loc, "Vorschau", "Preview")} className={styles.previewImg} />
              ) : (
                <div className={styles.fileRow}>
                  <Icon icon={FileText} decorative />
                  <span className={styles.fileName}>{file.name}</span>
                </div>
              )}
              <div className={styles.uploadActions}>
                <Button
                  variant="secondary"
                  onClick={() => { setFile(null); setPreview(null); }}
                >
                  <T de="Andere Datei" en="Different file" />
                </Button>
                <Button
                  variant="primary"
                  loading={loading}
                  onClick={upload}
                  leftIcon={<Icon icon={Sparkles} decorative />}
                >
                  <T de="Jetzt erklären" en="Explain now" />
                </Button>
              </div>
            </Stack>
          </Card>
        ) : (
          <EmptyState
            icon={<Icon icon={Upload} decorative />}
            title={tl(loc, "Klick hier oder zieh deine Lohnabrechnung rein", "Click here or drag your payslip in")}
            description={tl(loc, "PDF, PNG, JPG · max. 10 MB · für die KI-Analyse verschlüsselt an Anthropic übertragen; CareerKit speichert die Datei nicht dauerhaft.", "PDF, PNG, JPG · max 10 MB · encrypted in transit to Anthropic for AI analysis; CareerKit does not permanently store the file.")}
            action={
              <Button variant="primary" onClick={() => inputRef.current?.click()} leftIcon={<Icon icon={Upload} decorative />}>
                <T de="Datei auswählen" en="Choose file" />
              </Button>
            }
          />
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          hidden
          onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])}
        />

        {error && (
          <Tint tone="red">
            <div className={styles.inlineNote}>
              <Icon icon={AlertTriangle} decorative />
              <span>{error}</span>
            </div>
          </Tint>
        )}

        {/* Demo CTA */}
        {!file && (
          <Card variant="muted">
            <div className={styles.demoRow}>
              <p className={styles.demoText}>
                <T de="Erst mal anschauen, was du bekommst?" en="Want to see what you get first?" />
              </p>
              <Button variant="secondary" loading={loading} onClick={showExampleData} leftIcon={<Icon icon={Eye} decorative />}>
                <T de="Beispiel-Analyse anzeigen" en="Show example analysis" />
              </Button>
            </div>
          </Card>
        )}

        {/* What you get */}
        <div>
          <SectionHeader title={<T de="Was du bekommst" en="What you get" />} />
          <div className={styles.featureGrid}>
            {[
              { i: Receipt, tDe: "Jede Abzug-Position erklärt", tEn: "Every deduction explained", sDe: "Lohnsteuer, Soli, KV, RV, AV, alles auf einen Blick", sEn: "Income tax, solidarity surcharge, health/pension/unemployment insurance, all at a glance" },
              { i: TrendingUp, tDe: "Konkrete Spar-Tipps", tEn: "Concrete saving tips", sDe: "Wo du wirklich Geld zurückholen kannst", sEn: "Where you can actually get money back" },
              { i: Shield, tDe: "Auffälligkeiten gemeldet", tEn: "Irregularities flagged", sDe: "Falsche Steuerklasse, fehlende Beiträge etc.", sEn: "Wrong tax class, missing contributions etc." },
              { i: CheckCircle, tDe: "Was du dafür bekommst", tEn: "What you get for it", sDe: "Welche Leistungen deine Sozialabgaben sichern", sEn: "What benefits your social contributions secure" },
            ].map((b, i) => {
              const Ic = b.i;
              return (
                <Card key={i} variant="muted">
                  <div className={styles.featureItem}>
                    <span className={styles.featureIcon}><Icon icon={Ic} decorative /></span>
                    <div>
                      <p className={styles.featureTitle}>{tl(loc, b.tDe, b.tEn)}</p>
                      <p className={styles.featureSub}>{tl(loc, b.sDe, b.sEn)}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </Stack>
    </FinancePage>
  );
}
